import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { useApiClient } from '@/api/ApiClientProvider'
import { ATTRIBUTION_MODEL_LABELS, type FirstPartyTrafficQuery, type FirstPartyTrafficReport } from '@/api/firstPartyTraffic'
import { SectionFrame } from '@/app/pages'
import type { RouteDef } from '@/app/routes'
import { ChannelBarChart, channelBarRowsFromReport } from '@/components/ChannelBarChart'
import { CoverageIndicator } from '@/components/CoverageIndicator'
import { splitsNotTracked } from '@/components/dataQuality'
import { DataQualityNotice } from '@/components/DataQualityNotice'
import { Badge, Card, EmptyState, FreshnessBadge, LoadingBlock } from '@/design'
import { usePermissions } from '@/lib/access'
import { usePageFreshness } from '@/lib/freshness'
import type { GlobalFilters } from '@/lib/filters'

import { CsvButton } from './CsvButton'
import { firstPartyCsvFilename } from './download'
import { firstPartyErrorMessage, firstPartyExportErrorMessage } from './errors'
import { FirstPartyTable, FirstPartyTotals } from './FirstPartyTable'
import { firstPartySubtitle } from './firstPartyMetrics'
import { useAttributionModel, useChartMetric, useFirstPartyQuery, useFirstPartyReport, type FirstPartyReportResult } from './firstPartyQuery'
import { TmtSourceBadge } from './TrafficBadges'
import { ModelSelect } from './TrafficControls'

/** Where the connector's tracking script and consent are set up: what creates first-party visits. */
const INTEGRATIONS_ROUTE = '/integrations'
const UNAVAILABLE = 'First-party traffic is temporarily unavailable. Try again in a moment.'

/**
 * Analytics › Website with the TmT first-party source: the website's own consented
 * visits and orders, channel → source → medium → campaign → creative → landing page,
 * under a chosen attribution model (blueprint 05 §5.6, 10 §10.1). Every figure here is
 * TmT-attributed and labelled so; none is GA4's (06 §6.10).
 */
export function FirstPartyTraffic({
  route,
  filters,
  sourceSwitch,
}: {
  route: RouteDef
  filters: GlobalFilters
  sourceSwitch: ReactNode
}) {
  const client = useApiClient()
  const { can } = usePermissions()
  const [model, setModel] = useAttributionModel()
  const query = useFirstPartyQuery(filters, model)
  const result = useFirstPartyReport(query)
  const report = result.data
  const source = client.kind === 'http' ? 'live' : 'demo'
  usePageFreshness(report ? { source, lastUpdatedAt: report.freshness.newestTouchAt } : undefined)

  return (
    <SectionFrame
      route={route}
      actions={
        <span className="tr-source-group">
          <TmtSourceBadge model={model} lookbackDays={report?.lookbackDays ?? null} />
          <CoverageIndicator dataQuality={report?.dataQuality} />
        </span>
      }
    >
      <div className="ci-stack">
        <div className="tr-controls">
          {sourceSwitch}
          <ModelSelect value={model} onChange={setModel} />
          {report ? <FreshnessBadge meta={{ source, lastUpdatedAt: report.freshness.newestTouchAt }} /> : null}
          {report?.nonProduction ? <Badge tone="warning">Includes non-production websites</Badge> : null}
          {can('exports.create') && report && report.rows.length > 0 ? (
            <CsvButton
              fetchCsv={() => client.firstPartyTrafficCsv(query)}
              filename={firstPartyCsvFilename(query)}
              errorMessage={firstPartyExportErrorMessage}
            />
          ) : null}
        </div>
        <DataQualityNotice dataQuality={report?.dataQuality} />
        <FirstPartyBody result={result} query={query} />
      </div>
    </SectionFrame>
  )
}

/** Exactly one of: loading, error, no visits yet, or the chart and the drill-down. */
function FirstPartyBody({ result, query }: { result: FirstPartyReportResult; query: FirstPartyTrafficQuery }) {
  const report = result.data
  if (result.isPending) return <LoadingBlock rows={6} label="Loading first-party traffic" />
  if (!report) {
    return (
      <Card>
        <EmptyState variant="outage" title="Traffic could not be loaded" description={firstPartyErrorMessage(result.error, UNAVAILABLE)} announce />
      </Card>
    )
  }
  if (report.rows.length === 0) return <NoVisits report={report} />
  return (
    <>
      <ChartCard report={report} />
      <Card title="By channel" subtitle={firstPartySubtitle(report)}>
        <FirstPartyTotals report={report} />
        <FirstPartyTable
          report={report}
          base={query}
          hasNextPage={result.hasNextPage}
          isFetchingNextPage={result.isFetchingNextPage}
          fetchNextPage={result.fetchNextPage}
          nextPageError={result.nextPageError}
        />
        <p className="tr-note">
          {report.consentNote} A session with touches in two channels or campaigns counts in each of them, and once in the
          totals. Orders are credited to the channel of the touch the model chooses;{' '}
          {splitsNotTracked(report)
            ? `Not tracked orders had no tracked visit in the ${report.lookbackDays} days before them, and Direct visits are a channel of their own.`
            : `unattributed orders had no touch in the ${report.lookbackDays} days before them.`}
        </p>
      </Card>
    </>
  )
}

function ChartCard({ report }: { report: FirstPartyTrafficReport }) {
  const [metric, setMetric] = useChartMetric()
  return (
    <Card>
      <ChannelBarChart
        rows={channelBarRowsFromReport(report)}
        metric={metric}
        onMetricChange={setMetric}
        currency={report.currency}
        caption={`TmT first-party, ${ATTRIBUTION_MODEL_LABELS[report.model]} model`}
      />
    </Card>
  )
}

function NoVisits({ report }: { report: FirstPartyTrafficReport }) {
  return (
    <Card>
      <EmptyState
        variant="no-results"
        title="No first-party visits in this range yet"
        description={`Nothing was recorded from ${report.start} to ${report.end}. The connector's tracking script and visitors' analytics consent are what create first-party visits.`}
        action={
          <Link className="ci-button" to={INTEGRATIONS_ROUTE}>
            Check the connector in Integrations
          </Link>
        }
      />
    </Card>
  )
}
